<script setup lang="ts">
import type { Component } from "vue";

/**
 * EmptyState — full-area placeholder with icon, title, optional body,
 * and an optional CTA link.
 *
 * The CTA is modeled as a single optional `cta` object (label + to)
 * rather than two independent optional fields. Making it one unit
 * prevents "half-specified CTA" states (label without target, or
 * target without label) from being silently dropped by the template.
 */
defineProps<{
  /** Heroicon component (24/outline) — displayed centered above the title. */
  icon: Component;
  title: string;
  /** Body copy shown below the title. */
  body?: string;
  /** Optional CTA link. Both label and target are required when present. */
  cta?: { label: string; to: string };
}>();
</script>

<template>
  <div
    data-testid="empty-state"
    class="flex flex-col items-center justify-center text-center py-16 px-6 gap-3"
  >
    <component
      :is="icon"
      class="h-12 w-12 text-text-tertiary"
      aria-hidden="true"
    />
    <h3 data-testid="empty-state-title" class="text-base font-semibold text-text-primary mt-2">
      {{ title }}
    </h3>
    <p
      v-if="body"
      data-testid="empty-state-body"
      class="text-sm text-text-secondary max-w-md"
    >
      {{ body }}
    </p>
    <router-link
      v-if="cta"
      :to="{ name: cta.to }"
      data-testid="empty-state-cta"
      class="mt-2 inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover transition-colors"
    >
      {{ cta.label }}
    </router-link>
  </div>
</template>
